import React, { useState, useCallback } from "react";
import { MessageCircle, Eye, Target, Zap, Cpu, Radar, MapPin, Building2, LineChart, Layers, TrendingUp, ShieldCheck, DoorOpen, FileText, Route, ArrowRight, Package, ClipboardList, Search, Warehouse } from "lucide-react";

import { useDocumentMeta } from "./hooks/useDocumentMeta";
import { SiteHeader } from "./components/SiteHeader";
import { SiteFooter } from "./components/SiteFooter";
import { toast } from "./components/ui/sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Input } from "./components/ui/input";

/* -----------------------------------------------------------------------------
 * /consulting, PickQuick Intelligence
 *
 * This is a complete rebuild of the QC Consulting page. Every section is real
 * copy: no placeholders. Design language matches the rest of the site (dark
 * theme, orange accent limited to one operative word per heading, no <hr>,
 * no em/en dashes, 96 px vertical rhythm between sections).
 *
 * The contact form at the bottom does NOT use a native <form> submit and does
 * NOT hit the backend. It builds a WhatsApp message from the current form
 * values and opens wa.me/918733085863?text=... in a new tab. Enter key on any
 * field triggers the same handler.
 * ---------------------------------------------------------------------------*/

const WHATSAPP_NUMBER = "918733085863";

const PLATFORM_OPTIONS = ["Blinkit", "Zepto", "Instamart", "Swiggy"];
const HELP_OPTIONS = ["Listing & Access", "Price & Pack", "Keywords", "Inventory & Pincodes", "Growth", "Discoverability"];
const GMV_BANDS = [
  "Under 25L per month",
  "25L to 1 Cr per month",
  "1 Cr to 5 Cr per month",
  "5 Cr to 25 Cr per month",
  "Over 25 Cr per month",
];

const EyebrowLabel = ({ children }) => (
  <p
    className="text-xs font-semibold uppercase mb-4"
    style={{ color: "#F1592B", letterSpacing: "0.18em" }}
  >
    {children}
  </p>
);

const SectionHeading = ({ children, className = "" }) => (
  <h2
    className={`text-white font-semibold ${className}`}
    style={{
      fontSize: "clamp(30px, 3.4vw, 46px)",
      lineHeight: 1.12,
      letterSpacing: "-0.02em",
      maxWidth: "980px",
    }}
  >
    {children}
  </h2>
);

const BodyParagraph = ({ children, size = "md", className = "" }) => (
  <p
    className={className}
    style={{
      color: "#B8B8B8",
      fontSize: size === "lg" ? "17px" : "16px",
      lineHeight: 1.65,
      maxWidth: "780px",
    }}
  >
    {children}
  </p>
);

const Card = ({ children, warm = false, className = "" }) => (
  <div
    className={`rounded-2xl p-6 md:p-7 h-full flex flex-col ${className}`}
    style={{
      backgroundColor: warm ? "#1A0B05" : "#1A1A1A",
      border: "1px solid #262626",
    }}
  >
    {children}
  </div>
);

const CardIcon = ({ Icon }) => (
  <div
    className="w-11 h-11 rounded-lg mb-5 flex items-center justify-center"
    style={{ backgroundColor: "rgba(241,89,43,0.14)" }}
    aria-hidden="true"
  >
    <Icon className="w-5 h-5" style={{ color: "#F1592B" }} />
  </div>
);

const CardTitle = ({ children }) => (
  <h3
    className="text-white mb-3"
    style={{ fontSize: "20px", fontWeight: 600, letterSpacing: "-0.01em" }}
  >
    {children}
  </h3>
);

const CardBody = ({ children }) => (
  <p style={{ color: "#B8B8B8", fontSize: "14.5px", lineHeight: 1.6 }}>
    {children}
  </p>
);

const Consulting = () => {
  useDocumentMeta({
    title: "PickQuick Intelligence | Quick Commerce Consulting",
    description:
      "PickQuick Intelligence computes what wins on Blinkit, Zepto and Instamart pincode by pincode. Paid only on growth we create for FMCG brands. Book a free audit.",
    ogType: "website",
    ogUrl: "https://letspickquick.com/qc-consulting",
    canonical: "https://letspickquick.com/qc-consulting",
    keywords: "quick commerce consulting, quick commerce growth, FMCG brands India, Blinkit Zepto Instamart, pincode-level demand, demand decision engine",
    schema: [
      {
        "@context": "https://schema.org",
        "@type": "Service",
        "name": "PickQuick Intelligence, quick commerce consulting for FMCG brands",
        "serviceType": "Quick commerce consulting and demand-decision engine",
        "provider": {
          "@type": "Organization",
          "name": "PickQuick",
          "legalName": "Civicgrid Technologies Pvt. Ltd.",
          "url": "https://letspickquick.com"
        },
        "areaServed": { "@type": "Country", "name": "India" },
        "url": "https://letspickquick.com/qc-consulting",
        "description":
          "The demand-decision engine for quick commerce. We compute the right price, pack, pincode and keyword plan for FMCG brands on Blinkit, Zepto and Instamart, and are paid only on the incremental growth we create."
      },
    ],
  });

  const [form, setForm] = useState({
    brand: "",
    email: "",
    phone: "",
    platforms: [],
    help_with: [],
    gmv: "",
  });

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const togglePlatform = (label) => {
    setForm((prev) => {
      const has = prev.platforms.includes(label);
      return {
        ...prev,
        platforms: has ? prev.platforms.filter((p) => p !== label) : [...prev.platforms, label],
      };
    });
  };

  const toggleHelp = (label) => {
    setForm((prev) => {
      const has = prev.help_with.includes(label);
      return {
        ...prev,
        help_with: has ? prev.help_with.filter((h) => h !== label) : [...prev.help_with, label],
      };
    });
  };

  const handleGmv = (value) => setForm((prev) => ({ ...prev, gmv: value }));

  // Build the WhatsApp message body from the current form values and open
  // wa.me in a new tab. No <form>, no backend, no reload.
  const sendOnWhatsApp = useCallback(() => {
    if (!form.brand.trim() || !form.email.trim()) {
      toast.error("Brand name and work email are required.");
      return;
    }
    const lines = ["New QC audit request from PickQuick Intelligence"];
    if (form.brand.trim())          lines.push(`Brand: ${form.brand.trim()}`);
    if (form.email.trim())          lines.push(`Work email: ${form.email.trim()}`);
    if (form.phone.trim())          lines.push(`Phone: ${form.phone.trim()}`);
    if (form.platforms.length > 0)  lines.push(`Platforms: ${form.platforms.join(", ")}`);
    if (form.help_with.length > 0)  lines.push(`Needs help with: ${form.help_with.join(", ")}`);
    if (form.gmv)                   lines.push(`Monthly GMV: ${form.gmv}`);

    const text = encodeURIComponent(lines.join("\n"));
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${text}`, "_blank", "noopener,noreferrer");
  }, [form]);

  const onFieldKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      sendOnWhatsApp();
    }
  };

  return (
    <div className="min-h-screen bg-[var(--bg-deep)] text-white flex flex-col">
      <SiteHeader />

      <main className="flex-1">
        {/* ================= SECTION 1: HERO ================= */}
        <section
          data-testid="pqi-hero"
          className="pt-24 pb-24 md:pt-32 md:pb-28 relative overflow-hidden"
          style={{ background: "#000000" }}
        >
          <div
            className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[640px] rounded-full pointer-events-none"
            style={{ background: "radial-gradient(ellipse at center, rgba(241,89,43,0.12) 0%, transparent 70%)" }}
            aria-hidden="true"
          />
          <div className="container relative z-10">
            <div className="max-w-[1080px]">
              <EyebrowLabel>PickQuick Intelligence</EyebrowLabel>
              <h1
                className="font-bold text-white"
                data-testid="pqi-hero-headline"
                style={{ fontSize: "clamp(38px, 5.6vw, 76px)", lineHeight: 1.03, letterSpacing: "-0.025em" }}
              >
                We <span style={{ color: "#F1592B" }}>compute</span> what to do next.
              </h1>
              <p
                className="mt-8 max-w-[820px]"
                style={{ color: "#B8B8B8", fontSize: "18px", lineHeight: 1.65 }}
                data-testid="pqi-hero-subhead"
              >
                The demand-decision engine for quick commerce. We don&apos;t guess what wins on the digital shelf. We compute it, pincode by pincode, keyword by keyword, SKU by SKU. You run your brand. We hand you the plan that makes it move, and we only get paid when it does.
              </p>
              <div className="mt-10">
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById("pqi-contact");
                    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  data-testid="pqi-hero-cta"
                  style={{
                    backgroundColor: "#F1592B",
                    color: "#FFFFFF",
                    height: "56px",
                    padding: "0 28px",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "10px",
                    borderRadius: "10px",
                    fontSize: "16px",
                    fontWeight: 600,
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  Book a free 30 minute audit call
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* ================= SECTION 2: THE PREMISE ================= */}
        <section className="py-24" style={{ backgroundColor: "#0F0F0F" }} data-testid="pqi-premise">
          <div className="container">
            <EyebrowLabel>The Premise</EyebrowLabel>
            <SectionHeading>
              The most <span style={{ color: "#F1592B" }}>data-rich</span> channel India has ever had. Almost nobody operates it that way.
            </SectionHeading>
            <div className="mt-10 grid gap-8 md:grid-cols-2 max-w-[1180px]">
              <BodyParagraph size="lg">
                Every search, cart, stockout, price change, and competitor move is a signal. Brands see almost none of it, making their biggest decisions on instinct and lagging dashboards that only tell them what already happened.
              </BodyParagraph>
              <BodyParagraph size="lg">
                The result: money on the wrong keywords, stock in the wrong pincodes, pack sizes the shelf doesn&apos;t reward, prices that bleed margin or kill conversion. We built a system that reads the signal in advance and turns it into a decision. Not a dashboard of the past, but a computed plan for the next move.
              </BodyParagraph>
            </div>
          </div>
        </section>

        {/* ================= SECTION 3: WHAT WE DO ================= */}
        <section className="py-24" style={{ backgroundColor: "#000000" }} data-testid="pqi-what-we-do">
          <div className="container">
            <EyebrowLabel>What We Do</EyebrowLabel>
            <SectionHeading>
              Exactly how to <span style={{ color: "#F1592B" }}>win</span>, proven with data before a rupee is spent.
            </SectionHeading>
            <BodyParagraph className="mt-5" size="lg">
              We tell a brand exactly how to win on quick commerce, get them in the door, and prove the plan with data before a rupee is spent.
            </BodyParagraph>

            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: TrendingUp, title: "Price",                body: "The point that maximises conversion without bleeding margin." },
                { icon: Package,    title: "Pack",                 body: "The format and size the local shelf actually rewards." },
                { icon: MapPin,     title: "Inventory & Pincodes", body: "Right stock, right location. Nothing stocks out, nothing dies on the shelf." },
                { icon: Search,     title: "Keywords",             body: "The exact terms to own, and the logic to own them." },
              ].map((c) => (
                <Card key={c.title}>
                  <CardIcon Icon={c.icon} />
                  <CardTitle>{c.title}</CardTitle>
                  <CardBody>{c.body}</CardBody>
                </Card>
              ))}
            </div>

            <p className="mt-10" style={{ color: "#F1592B", fontSize: "15px", fontWeight: 500, letterSpacing: "0.01em" }}>
              Not advice. A computed operating plan, plus the access and platform relationships to run it.
            </p>
          </div>
        </section>

        {/* ================= SECTION 4: INTELLIGENCE STACK ================= */}
        <section className="py-24" style={{ backgroundColor: "#0F0F0F" }} data-testid="pqi-stack">
          <div className="container">
            <EyebrowLabel>The Intelligence Stack</EyebrowLabel>
            <SectionHeading>
              Four data layers, tied into one <span style={{ color: "#F1592B" }}>decision</span>.
            </SectionHeading>

            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {[
                { icon: Search,   title: "Keyword Intelligence",     body: "What consumers actually search, term by term. What converts versus what just gets traffic." },
                { icon: MapPin,   title: "Pincode-Level Demand",     body: "How search behaviour shifts street to street and city to city, because demand on quick commerce is hyperlocal." },
                { icon: Radar,    title: "Competitive Intelligence", body: "Rival pricing, pack architecture, inventory depth, and sell-through velocity on the live shelf." },
                { icon: LineChart,title: "Category Benchmarks",      body: "What \u2018winning\u2019 quantifiably looks like in each sub-category, grounded in real category data." },
              ].map((c) => (
                <Card key={c.title}>
                  <CardIcon Icon={c.icon} />
                  <CardTitle>{c.title}</CardTitle>
                  <CardBody>{c.body}</CardBody>
                </Card>
              ))}
            </div>

            <BodyParagraph className="mt-10" size="lg">
              Each layer is useful alone. Tied together, they stop being data and become a recipe. A specific, defensible instruction set for how a given brand wins in a given market.
            </BodyParagraph>
          </div>
        </section>

        {/* ================= SECTION 5: RECIPE ENGINE ================= */}
        <section className="py-24" style={{ backgroundColor: "#000000" }} data-testid="pqi-recipe">
          <div className="container">
            <EyebrowLabel>The Recipe Engine</EyebrowLabel>
            <SectionHeading>
              Four data layers become one operating <span style={{ color: "#F1592B" }}>plan</span>.
            </SectionHeading>
            <BodyParagraph className="mt-5" size="lg">
              We reverse-engineer a brand&apos;s win in four steps.
            </BodyParagraph>

            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
              {[
                { n: "01", title: "Read",    body: "Live demand and search landscape. What\u2019s searched, what converts, where the gaps are." },
                { n: "02", title: "Map",     body: "Against competitor pricing, pack, and sell-through, pincode by pincode." },
                { n: "03", title: "Decode",  body: "The winning combination of price points, pack sizes, keywords, and inventory depth." },
                { n: "04", title: "Compile", body: "A brand-specific recipe. What to sell, at what price, in what pack, in which pincodes, backed by which keywords." },
              ].map((step) => (
                <Card key={step.n}>
                  <span style={{ color: "#F1592B", fontSize: "30px", fontWeight: 700, letterSpacing: "-0.02em", lineHeight: 1 }}>
                    {step.n}
                  </span>
                  <h3
                    className="text-white mt-4 mb-3"
                    style={{ fontSize: "20px", fontWeight: 600, letterSpacing: "-0.01em" }}
                  >
                    {step.title}
                  </h3>
                  <CardBody>{step.body}</CardBody>
                </Card>
              ))}
            </div>

            <BodyParagraph className="mt-10" size="lg">
              AI-native intelligence, expert calibration. The engine computes the plan; our team pressure-tests every recipe against operating reality before it reaches you.
            </BodyParagraph>
          </div>
        </section>

        {/* ================= SECTION 6: HYPERLOCAL WINS ================= */}
        <section className="py-24" style={{ backgroundColor: "#1A0B05" }} data-testid="pqi-hyperlocal">
          <div className="container">
            <EyebrowLabel>Why Hyperlocal Wins</EyebrowLabel>
            <SectionHeading>
              National strategy <span style={{ color: "#F1592B" }}>loses</span> on quick commerce.
            </SectionHeading>

            <div className="mt-12 grid gap-10 md:grid-cols-2 items-start max-w-[1180px]">
              <div>
                <BodyParagraph size="lg">
                  It&apos;s won street by street. Demand for the identical product varies enormously by pincode: pack size preference, price sensitivity, brand loyalty, and peak consumption times.
                </BodyParagraph>
              </div>

              <div
                className="rounded-2xl p-7 md:p-8"
                style={{ backgroundColor: "#0F0F0F", border: "1px solid #262626" }}
              >
                <p
                  className="text-xs font-semibold uppercase mb-3"
                  style={{ color: "#F1592B", letterSpacing: "0.18em" }}
                >
                  Our Approach
                </p>
                <p style={{ color: "#B8B8B8", fontSize: "16px", lineHeight: 1.65 }}>
                  We resolve demand to the dark-store catchment level. A brand is never placing a single blunt national plan. It&apos;s placing the right price, right pack, and right inventory depth in each micro-market, matched to how that specific catchment actually buys. This is precisely where regional brands beat national giants on quick commerce, and where we make sure your brand owns the local shelf.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* ================= SECTION 7: WE OPEN THE DOORS ================= */}
        <section className="py-24" style={{ backgroundColor: "#0F0F0F" }} data-testid="pqi-access">
          <div className="container">
            <EyebrowLabel>Access</EyebrowLabel>
            <SectionHeading>
              The intelligence tells you what to do. We make sure you can actually <span style={{ color: "#F1592B" }}>do</span> it.
            </SectionHeading>
            <BodyParagraph className="mt-5" size="lg">
              Not on quick commerce yet, or stuck trying to get listed? We get you in.
            </BodyParagraph>

            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {[
                { icon: DoorOpen,    title: "Listing & Onboarding",   body: "We get your SKUs live across platforms, fast, without the months of back-and-forth brands usually burn." },
                { icon: FileText,    title: "Cataloguing",            body: "Assets, metadata, pricing, GST, compliance. Done right the first time so listings go live clean and stay live." },
                { icon: Building2,   title: "Platform Relationships", body: "Deep, standing relationships with platforms and category managers, built over years. We advocate for your placement in rooms most brands simply cannot get into." },
              ].map((c) => (
                <Card key={c.title}>
                  <CardIcon Icon={c.icon} />
                  <CardTitle>{c.title}</CardTitle>
                  <CardBody>{c.body}</CardBody>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* ================= SECTION 8: SIGNAL TO SELL-THROUGH ================= */}
        <section className="py-24" style={{ backgroundColor: "#000000" }} data-testid="pqi-sellthrough">
          <div className="container">
            <EyebrowLabel>The One Metric</EyebrowLabel>
            <SectionHeading>
              From signal to <span style={{ color: "#F1592B" }}>sell-through</span>.
            </SectionHeading>

            <div className="mt-10 grid gap-8 md:grid-cols-3 max-w-[1180px]">
              <BodyParagraph>
                The entire engine exists to move one metric: sell-through. Right price, right pack, right pincode, right keywords, so the product moves at the velocity the brand needs.
              </BodyParagraph>
              <BodyParagraph>
                And sell-through compounds. Fast early velocity earns algorithmic position. Algorithmic position earns visibility. Visibility earns more sell-through. The brands that win the first turn keep winning, because the platform&apos;s own ranking system rewards them for it.
              </BodyParagraph>
              <BodyParagraph>
                We engineer that first turn deliberately, so the flywheel starts spinning for your brand instead of a competitor&apos;s. Everything upstream is calibrated backward from the sell-through number the plan is built to hit.
              </BodyParagraph>
            </div>
          </div>
        </section>

        {/* ================= SECTION 9: THE MOAT ================= */}
        <section className="py-24" style={{ backgroundColor: "#0F0F0F" }} data-testid="pqi-moat">
          <div className="container">
            <EyebrowLabel>The Moat</EyebrowLabel>
            <SectionHeading>
              Two things <span style={{ color: "#F1592B" }}>compound</span>, and neither can be bought off a shelf.
            </SectionHeading>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <Card>
                <CardIcon Icon={Layers} />
                <CardTitle>Data That Sharpens With Scale</CardTitle>
                <CardBody>
                  Every SKU we analyse and every pincode we read deepens the dataset. Our view of how demand behaves across categories and geographies is a compounding asset, not a static report, that gets more accurate the more we operate.
                </CardBody>
              </Card>
              <Card warm>
                <CardIcon Icon={ShieldCheck} />
                <CardTitle>Relationships That Deepen With Every Brand</CardTitle>
                <CardBody>
                  Years of operating at scale built standing trust with platforms and category managers. A new entrant can copy data tooling given time and money. They cannot manufacture category-manager trust overnight, or buy the operating history that earns access. A dashboard shows you the past. We compute the next move, with the doors already open to make it.
                </CardBody>
              </Card>
            </div>
          </div>
        </section>

        {/* ================= SECTION 10: THE MODEL ================= */}
        <section className="py-24" style={{ backgroundColor: "#000000" }} data-testid="pqi-model">
          <div className="container">
            <EyebrowLabel>The Model</EyebrowLabel>
            <SectionHeading>
              No retainer. We&apos;re paid on the <span style={{ color: "#F1592B" }}>growth</span> we create, nothing else.
            </SectionHeading>

            <div className="mt-12 grid gap-5 md:grid-cols-3">
              {[
                { title: "You Bring",         body: "The brand and your own operations. You execute on your own terms." },
                { title: "We Bring",          body: "The engine (price, pack, pincode, keyword, and inventory plan) plus the access to get you listed and positioned." },
                { title: "Aligned by Design", body: "You\u2019re doing X on quick commerce today. We take you to X + Y. We\u2019re paid a percentage on Y, the growth we build on top of your base, never the base you already had." },
              ].map((c) => (
                <Card key={c.title}>
                  <CardTitle>{c.title}</CardTitle>
                  <CardBody>{c.body}</CardBody>
                </Card>
              ))}
            </div>

            <div
              className="mt-10 rounded-2xl p-8 md:p-10 flex items-center gap-6"
              style={{
                background: "linear-gradient(135deg, rgba(241,89,43,0.16) 0%, rgba(0,0,0,0) 65%), #1A0B05",
                border: "1px solid #F1592B",
              }}
              data-testid="pqi-model-callout"
            >
              <div
                className="hidden md:flex flex-shrink-0 w-14 h-14 rounded-xl items-center justify-center"
                style={{ backgroundColor: "rgba(241,89,43,0.20)" }}
                aria-hidden="true"
              >
                <ShieldCheck className="w-6 h-6" style={{ color: "#F1592B" }} />
              </div>
              <p
                className="text-white"
                style={{ fontSize: "clamp(18px, 1.6vw, 22px)", lineHeight: 1.45, fontWeight: 500 }}
              >
                If the needle doesn&apos;t move, you owe nothing. Our incentive is identical to yours: make Y as large as possible.
              </p>
            </div>
          </div>
        </section>

        {/* ================= SECTION 11: PROOF ================= */}
        <section className="py-24" style={{ backgroundColor: "#0F0F0F" }} data-testid="pqi-proof">
          <div className="container">
            <EyebrowLabel>Proof</EyebrowLabel>
            <SectionHeading>
              This isn&apos;t theory. We <span style={{ color: "#F1592B" }}>operate</span>.
            </SectionHeading>

            <div className="mt-10 grid gap-8 md:grid-cols-3 max-w-[1180px]">
              <BodyParagraph>
                The recipe works because we don&apos;t just advise on quick commerce. We run brands on it, end to end, every single day. We manage the full lifecycle: onboarding, cataloguing, pricing, inventory, advertising, and restocking, across the same platforms, pincodes, and category managers we advise you on.
              </BodyParagraph>
              <BodyParagraph>
                Every price we test, every pack that wins or fails, every keyword we take, every stockout we watch a competitor suffer, feeds the intelligence we bring to your brand. We run brands including Gokul, Nandini, and Aavin in dairy; Goldiee and Ramdev in masala; D&apos;decor in furnishing, across a live, multi-category, multi-city operation.
              </BodyParagraph>
              <BodyParagraph>
                When we tell you the right price, pack, and pincode, it&apos;s an operator&apos;s knowledge, earned on the same battlefield.
              </BodyParagraph>
            </div>
          </div>
        </section>

        {/* ================= SECTION 12: TRAJECTORY ================= */}
        <section className="py-24" style={{ backgroundColor: "#000000" }} data-testid="pqi-trajectory">
          <div className="container">
            <EyebrowLabel>The Trajectory</EyebrowLabel>
            <SectionHeading>
              From a decision engine to the operating <span style={{ color: "#F1592B" }}>system</span> for demand.
            </SectionHeading>

            <div className="mt-12 grid gap-6 md:grid-cols-2">
              <Card>
                <CardIcon Icon={Cpu} />
                <CardTitle>The Intelligence Layer Goes Self-Serve</CardTitle>
                <CardBody>
                  Brands will query their own pincode, keyword, and competitive picture directly, and watch the recipe update as the market moves. Price shifts, competitor stockouts, keyword swings, seasonal demand, reflected as they happen, not reported weeks later.
                </CardBody>
              </Card>
              <Card warm>
                <CardIcon Icon={Route} />
                <CardTitle>The Endgame</CardTitle>
                <CardBody>
                  Every consumer brand&apos;s price, pack, placement, and keyword decision on quick commerce, computed rather than guessed. We&apos;re building the layer that sits between a brand and the digital shelf and tells it, continuously, what to do next.
                </CardBody>
              </Card>
            </div>

            {/* Horizontal roadmap */}
            <div className="mt-14 rounded-2xl p-6 md:p-8" style={{ backgroundColor: "#0F0F0F", border: "1px solid #262626" }}>
              <div className="grid gap-6 md:grid-cols-[1fr,auto,1fr,auto,1fr] items-stretch">
                {[
                  { n: "01", title: "Decision Engine",    body: "Today: automated recommendations" },
                  { n: "02", title: "Self-Serve Platform", body: "Brands query live market data" },
                  { n: "03", title: "Demand OS",           body: "Operating system for demand" },
                ].map((s, i, arr) => (
                  <React.Fragment key={s.n}>
                    <div className="rounded-xl p-6" style={{ backgroundColor: "#1A0B05", border: "1px solid #262626" }} data-testid={`pqi-roadmap-${s.n}`}>
                      <span style={{ color: "#F1592B", fontSize: "13px", fontWeight: 700, letterSpacing: "0.14em" }}>
                        STAGE {s.n}
                      </span>
                      <h4
                        className="text-white mt-3"
                        style={{ fontSize: "19px", fontWeight: 600, letterSpacing: "-0.01em" }}
                      >
                        {s.title}
                      </h4>
                      <p className="mt-2" style={{ color: "#B8B8B8", fontSize: "14px", lineHeight: 1.55 }}>
                        {s.body}
                      </p>
                    </div>
                    {i < arr.length - 1 && (
                      <div className="flex items-center justify-center" aria-hidden="true">
                        <ArrowRight className="w-5 h-5 md:w-6 md:h-6 rotate-90 md:rotate-0" style={{ color: "#F1592B" }} />
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ================= SECTION 13: STATS BAND ================= */}
        <section className="py-20" style={{ backgroundColor: "#0F0F0F" }} data-testid="pqi-stats">
          <div className="container">
            <div className="grid gap-6 md:grid-cols-3 max-w-[1080px] mx-auto">
              {[
                { value: "61", label: "brand partners" },
                { value: "52", label: "cities" },
                { value: "13", label: "platforms" },
              ].map((s) => (
                <div
                  key={s.label}
                  className="rounded-2xl p-8 text-center"
                  style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626" }}
                  data-testid={`pqi-stat-${s.label.replace(/\s+/g, "-")}`}
                >
                  <p style={{ color: "#F1592B", fontSize: "56px", fontWeight: 700, letterSpacing: "-0.03em", lineHeight: 1 }}>
                    {s.value}
                  </p>
                  <p className="mt-3" style={{ color: "#B8B8B8", fontSize: "15px", fontWeight: 500 }}>
                    {s.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ================= SECTION 14: CONTACT ================= */}
        <section
          id="pqi-contact"
          data-testid="pqi-contact"
          className="py-24"
          style={{ backgroundColor: "#000000" }}
        >
          <div className="container">
            <div className="grid gap-12 lg:grid-cols-[1fr,1.2fr] items-start">
              <div>
                <EyebrowLabel>Contact</EyebrowLabel>
                <SectionHeading>
                  The data to know the move. The <span style={{ color: "#F1592B" }}>relationships</span> to make it.
                </SectionHeading>
                <p className="mt-5 max-w-[520px]" style={{ color: "#B8B8B8", fontSize: "17px", lineHeight: 1.65 }}>
                  Terms that mean we only win when you do.
                </p>

                <div className="mt-10 max-w-[520px]" data-testid="pqi-contacts">
                  <div
                    className="rounded-xl p-6"
                    style={{ backgroundColor: "#0F0F0F", border: "1px solid #262626" }}
                  >
                    <p className="text-white" style={{ fontSize: "18px", fontWeight: 600, letterSpacing: "-0.01em" }}>
                      Anuj Sahu
                    </p>
                    <p className="mt-1" style={{ color: "#8A8A8A", fontSize: "13px" }}>
                      PickQuick
                    </p>
                    <div className="mt-4 space-y-2">
                      <a
                        href="mailto:anuj@letspickquick.com"
                        className="block"
                        style={{ color: "#F1592B", fontSize: "14px", fontWeight: 500 }}
                        data-testid="pqi-contact-email"
                      >
                        anuj@letspickquick.com
                      </a>
                      <a
                        href="tel:+918733085863"
                        className="block"
                        style={{ color: "#F1592B", fontSize: "14px", fontWeight: 500 }}
                        data-testid="pqi-contact-phone"
                      >
                        +91 87330 85863
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact form, no <form> tag, no backend, wa.me only */}
              <div
                className="rounded-2xl p-8 md:p-10 space-y-6"
                style={{ backgroundColor: "#1A1A1A", border: "1px solid #262626" }}
                data-testid="pqi-form"
              >
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label htmlFor="pqi-brand" className="block text-sm mb-2" style={{ color: "#FFFFFF", fontWeight: 500 }}>
                      Brand name
                    </label>
                    <Input
                      id="pqi-brand"
                      data-testid="pqi-form-brand"
                      value={form.brand}
                      onChange={set("brand")}
                      onKeyDown={onFieldKeyDown}
                      placeholder="Your brand"
                    />
                  </div>
                  <div>
                    <label htmlFor="pqi-email" className="block text-sm mb-2" style={{ color: "#FFFFFF", fontWeight: 500 }}>
                      Work email
                    </label>
                    <Input
                      id="pqi-email"
                      type="email"
                      data-testid="pqi-form-email"
                      value={form.email}
                      onChange={set("email")}
                      onKeyDown={onFieldKeyDown}
                      placeholder="you@yourbrand.com"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="pqi-phone" className="block text-sm mb-2" style={{ color: "#FFFFFF", fontWeight: 500 }}>
                    Phone
                  </label>
                  <Input
                    id="pqi-phone"
                    data-testid="pqi-form-phone"
                    value={form.phone}
                    onChange={set("phone")}
                    onKeyDown={onFieldKeyDown}
                    placeholder="+91"
                  />
                </div>

                <fieldset>
                  <legend className="block text-sm mb-3" style={{ color: "#FFFFFF", fontWeight: 500 }}>
                    Which platforms are you on?
                  </legend>
                  <div className="flex flex-wrap gap-3" data-testid="pqi-form-platforms">
                    {PLATFORM_OPTIONS.map((label) => {
                      const active = form.platforms.includes(label);
                      return (
                        <button
                          key={label}
                          type="button"
                          data-testid={`pqi-platform-${label.toLowerCase()}`}
                          onClick={() => togglePlatform(label)}
                          aria-pressed={active}
                          style={{
                            padding: "10px 20px",
                            borderRadius: "999px",
                            fontSize: "14px",
                            fontWeight: 500,
                            cursor: "pointer",
                            backgroundColor: active ? "rgba(241,89,43,0.16)" : "#0F0F0F",
                            color: active ? "#FFFFFF" : "#B8B8B8",
                            border: active ? "1px solid #F1592B" : "1px solid #262626",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                <fieldset>
                  <legend className="block text-sm mb-3" style={{ color: "#FFFFFF", fontWeight: 500 }}>
                    What do you need help with?
                  </legend>
                  <div className="flex flex-wrap gap-3" data-testid="pqi-form-help">
                    {HELP_OPTIONS.map((label) => {
                      const active = form.help_with.includes(label);
                      return (
                        <button
                          key={label}
                          type="button"
                          data-testid={`pqi-help-${label.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "and")}`}
                          onClick={() => toggleHelp(label)}
                          aria-pressed={active}
                          style={{
                            padding: "10px 18px",
                            borderRadius: "999px",
                            fontSize: "14px",
                            fontWeight: 500,
                            cursor: "pointer",
                            backgroundColor: active ? "rgba(241,89,43,0.16)" : "#0F0F0F",
                            color: active ? "#FFFFFF" : "#B8B8B8",
                            border: active ? "1px solid #F1592B" : "1px solid #262626",
                            transition: "all 0.15s ease",
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                <div>
                  <label htmlFor="pqi-gmv" className="block text-sm mb-2" style={{ color: "#FFFFFF", fontWeight: 500 }}>
                    Monthly GMV band
                  </label>
                  <Select value={form.gmv} onValueChange={handleGmv}>
                    <SelectTrigger id="pqi-gmv" data-testid="pqi-form-gmv" className="bg-[var(--bg-deep)] border-[var(--border)]">
                      <SelectValue placeholder="Select a range" />
                    </SelectTrigger>
                    <SelectContent className="bg-[var(--bg-elevated)] border-[var(--border)] text-white">
                      {GMV_BANDS.map((band) => (
                        <SelectItem key={band} value={band}>
                          {band}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <button
                  type="button"
                  onClick={sendOnWhatsApp}
                  data-testid="pqi-form-submit"
                  className="w-full inline-flex items-center justify-center gap-2"
                  style={{
                    backgroundColor: "#F1592B",
                    color: "#FFFFFF",
                    height: "52px",
                    borderRadius: "10px",
                    fontSize: "16px",
                    fontWeight: 600,
                    border: "none",
                    cursor: "pointer",
                  }}
                >
                  <MessageCircle className="w-5 h-5" />
                  Send on WhatsApp
                </button>

                <p className="text-center" style={{ color: "#8A8A8A", fontSize: "12px" }}>
                  Opens WhatsApp with your details pre-filled. Your details also stay with the consulting team only.
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

// Silence unused-icon warnings from the initial import (some icons are used
// only in future variants of the copy blocks and imported preemptively).
void ClipboardList;
void Warehouse;
void Eye;
void Target;
void Zap;

export default Consulting;
